import React, { useEffect } from "react";
import { X } from "lucide-react";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  closeOnOverlayClick?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = "md",
  closeOnOverlayClick = true,
}) => {
  const sizeClasses = {
    sm: "max-w-md",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closeOnOverlayClick) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0"
        onClick={handleOverlayClick}
      >
        {/* Overlay */}
        <div className="fixed inset-0 bg-canvas/70 backdrop-blur-sm" />

        {/* Modal panel */}
        <div
          className={`relative transform overflow-hidden bg-surface border border-white/[.08] shadow-panel text-left rounded-card sm:my-8 sm:w-full ${sizeClasses[size]}`}
        >
          {/* Header */}
          {title && (
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/[.08]">
              <h3 className="text-lg font-bold text-fg-bright">{title}</h3>
              <button
                onClick={onClose}
                className="p-2 min-h-[44px] min-w-[44px] text-fg-muted hover:text-fg-bright focus:outline-none transition-colors duration-200"
              >
                <span className="sr-only">Sluiten</span>
                <X className="h-6 w-6" />
              </button>
            </div>
          )}

          {/* Content */}
          <div className={title ? "px-6 py-4" : "p-6"}>{children}</div>
        </div>
      </div>
    </div>
  );
};

export default Modal;
